import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "../lib/supabaseClient";
import { Calendar, X, Loader2, AlertCircle, Check, CalendarDays, Trash2, CalendarRange, Clock, CalendarClock } from "lucide-react";
import { format, parseISO, eachDayOfInterval, isWithinInterval, areIntervalsOverlapping } from "date-fns";
import { Button } from "./ui/button";

export default function BlockedDatesManager({ onClose }) {
  const [blockedDates, setBlockedDates] = useState([]);
  const [blockedRanges, setBlockedRanges] = useState([]);
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [blockType, setBlockType] = useState('single'); // 'single', 'range', or 'time'
  const [selectedDate, setSelectedDate] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [timeBlockData, setTimeBlockData] = useState({
    date: '',
    time: '',
    reason: ''
  });
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  // Available time slots (matching home.jsx)
  const timeSlots = [
    '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM', 
    '10:00 PM', '11:00 PM', '12:00 AM', '1:00 AM'
  ];

  // Fetch all blocked data
  const fetchBlockedData = async () => {
    try {
      setLoading(true);
      
      // Fetch single blocked dates
      const { data: dates, error: datesError } = await supabase
        .from('blocked_dates')
        .select('*')
        .or('is_range.is.null,is_range.eq.false')
        .order('date', { ascending: true, nullsLast: true });
      
      if (datesError) throw datesError;
      
      // Fetch date ranges
      const { data: ranges, error: rangesError } = await supabase
        .from('blocked_dates')
        .select('*')
        .eq('is_range', true)
        .order('start_date', { ascending: true });
      
      if (rangesError) throw rangesError;
      
      // Fetch blocked times
      const { data: times, error: timesError } = await supabase
        .from('blocked_times')
        .select('*')
        .order('date', { ascending: true })
        .order('time', { ascending: true });
      
      if (timesError) throw timesError;
      
      setBlockedDates(dates || []);
      setBlockedRanges(ranges || []);
      setBlockedTimes(times || []);
    } catch (error) {
      console.error('Error fetching blocked data:', error);
      setError('Failed to load blocked dates and times');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlockedData();
  }, []);

  // Check if a time slot is already blocked
  const checkTimeBlocked = async (date, time) => {
    const { data: existingTime } = await supabase
      .from('blocked_times')
      .select('id')
      .eq('date', date)
      .eq('time', time)
      .maybeSingle();
    
    return !!existingTime;
  };

  // Check if a date range overlaps with existing blocked dates/ranges
  const checkRangeOverlap = (start, end) => {
    const newRangeStart = new Date(start);
    const newRangeEnd = new Date(end);
    
    // Check against single blocked dates
    const overlappingDates = blockedDates.filter(item => {
      const blockedDate = new Date(item.date);
      return isWithinInterval(blockedDate, { start: newRangeStart, end: newRangeEnd });
    });
    
    if (overlappingDates.length > 0) {
      return {
        overlaps: true,
        message: `Range overlaps with ${overlappingDates.length} blocked date(s)`
      };
    }
    
    // Check against existing ranges
    const overlappingRanges = blockedRanges.filter(item => {
      const existingStart = new Date(item.start_date);
      const existingEnd = new Date(item.end_date);
      
      return areIntervalsOverlapping(
        { start: newRangeStart, end: newRangeEnd },
        { start: existingStart, end: existingEnd }
      );
    });
    
    if (overlappingRanges.length > 0) {
      return {
        overlaps: true,
        message: `Range overlaps with ${overlappingRanges.length} existing blocked range(s)`
      };
    }
    
    return { overlaps: false };
  };

  // Check if a single date is already blocked
  const checkDateBlocked = async (date) => {
    // Check single dates
    const { data: existingDate } = await supabase
      .from('blocked_dates')
      .select('id')
      .eq('date', date)
      .or('is_range.is.null,is_range.eq.false')
      .maybeSingle();
    
    if (existingDate) return true;
    
    // Check if date falls within any range
    const { data: inRange } = await supabase
      .from('blocked_dates')
      .select('id')
      .eq('is_range', true)
      .lte('start_date', date)
      .gte('end_date', date)
      .maybeSingle();
    
    return !!inRange;
  };

  // Add blocked date, range, or time
  const handleAddBlocked = async (e) => {
    e.preventDefault();
    
    // Validation based on block type
    if (blockType === 'single' && !selectedDate) {
      setError('Please select a date');
      return;
    }
    
    if (blockType === 'range' && (!dateRange.start || !dateRange.end)) {
      setError('Please select both start and end dates');
      return;
    }
    
    if (blockType === 'range' && new Date(dateRange.start) > new Date(dateRange.end)) {
      setError('End date must be after start date');
      return;
    }

    if (blockType === 'time') {
      if (!timeBlockData.date) {
        setError('Please select a date');
        return;
      }
      if (!timeBlockData.time) {
        setError('Please select a time slot');
        return;
      }
    }

    setSubmitting(true);
    setError('');
    setSuccess('');
    
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      if (blockType === 'single') {
        // Check if date is already blocked
        const isBlocked = await checkDateBlocked(selectedDate);
        
        if (isBlocked) {
          setError('This date is already blocked');
          setSubmitting(false);
          return;
        }

        const { error } = await supabase
          .from('blocked_dates')
          .insert({
            date: selectedDate,
            reason: reason.trim() || null,
            created_by: user?.id,
            is_range: false
          });

        if (error) throw error;
        
        setSuccess('Date blocked successfully');
        setSelectedDate('');
      } else if (blockType === 'range') {
        // Check for overlaps
        const overlap = checkRangeOverlap(dateRange.start, dateRange.end);
        
        if (overlap.overlaps) {
          setError(overlap.message);
          setSubmitting(false);
          return;
        }

        // Calculate number of days in range
        const daysInRange = eachDayOfInterval({
          start: new Date(dateRange.start),
          end: new Date(dateRange.end)
        }).length;

        const { error } = await supabase
          .from('blocked_dates')
          .insert({
            start_date: dateRange.start,
            end_date: dateRange.end,
            reason: reason.trim() || null,
            created_by: user?.id,
            is_range: true,
            range_days: daysInRange,
            date: null
          });

        if (error) throw error;
        
        setSuccess(`${daysInRange} days blocked successfully`);
        setDateRange({ start: '', end: '' });
      } else if (blockType === 'time') {
        // Check if time slot is already blocked for this date
        const isBlocked = await checkTimeBlocked(timeBlockData.date, timeBlockData.time);
        
        if (isBlocked) {
          setError('This time slot is already blocked for this date');
          setSubmitting(false);
          return;
        }

        const { error } = await supabase
          .from('blocked_times')
          .insert({
            date: timeBlockData.date,
            time: timeBlockData.time,
            reason: timeBlockData.reason.trim() || null,
            created_by: user?.id
          });

        if (error) throw error;
        
        setSuccess(`Time slot ${timeBlockData.time} blocked successfully for ${format(parseISO(timeBlockData.date), 'MMMM d, yyyy')}`);
        setTimeBlockData({ date: '', time: '', reason: '' });
      }
      
      setReason('');
      fetchBlockedData();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error blocking:', error);
      setError(error.message || 'Failed to block');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete blocked date, range, or time
  const handleDelete = async (id, type) => {
    let typeName = 'item';
    if (type === 'date') typeName = 'date';
    else if (type === 'range') typeName = 'date range';
    else if (type === 'time') typeName = 'time slot';
    
    if (!window.confirm(`Are you sure you want to unblock this ${typeName}?`)) return;
    
    setDeletingId(id);
    try {
      if (type === 'time') {
        const { error } = await supabase
          .from('blocked_times')
          .delete()
          .eq('id', id);

        if (error) throw error;
        
        setBlockedTimes(blockedTimes.filter(t => t.id !== id));
      } else if (type === 'range') {
        const { error } = await supabase
          .from('blocked_dates')
          .delete()
          .eq('id', id);

        if (error) throw error;
        
        setBlockedRanges(blockedRanges.filter(d => d.id !== id));
      } else {
        const { error } = await supabase
          .from('blocked_dates')
          .delete()
          .eq('id', id);

        if (error) throw error;
        
        setBlockedDates(blockedDates.filter(d => d.id !== id));
      }
      
      setSuccess(`${typeName.charAt(0).toUpperCase() + typeName.slice(1)} unblocked successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting:', error);
      setError('Failed to unblock');
    } finally {
      setDeletingId(null);
    }
  };

  // Get min date for input (today)
  const getMinDate = () => {
    const today = new Date();
    return format(today, 'yyyy-MM-dd');
  };

  // Group blocked times by date for display
  const groupedBlockedTimes = () => {
    const grouped = {};
    blockedTimes.forEach(item => {
      const dateKey = item.date;
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: item.date,
          times: []
        };
      }
      grouped[dateKey].times.push(item);
    });
    return Object.values(grouped).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-[#8B2635] to-[#B23B4A] text-white p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CalendarClock className="w-6 h-6" />
                <h2 className="text-xl font-bold">Manage Blocked Dates & Times</h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
            {/* Success/Error Messages */}
            <AnimatePresence>
              {success && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="mb-4 p-3 bg-emerald-50 text-emerald-700 rounded-lg flex items-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  {success}
                </motion.div>
              )}
              
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2"
                >
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Add New Blocked Item Form */}
            <form onSubmit={handleAddBlocked} className="mb-8 p-4 bg-[#F9F9F9] rounded-lg border border-[#E5E5E5]">
              <h3 className="text-lg font-semibold text-[#2D2D2D] mb-4">Block Dates / Times</h3>
              
              {/* Block Type Toggle - Now with 3 options */}
              <div className="flex flex-wrap gap-4 mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="single"
                    checked={blockType === 'single'}
                    onChange={(e) => setBlockType(e.target.value)}
                    className="text-[#8B2635] focus:ring-[#8B2635]"
                  />
                  <span className="text-sm text-[#2D2D2D]">Single Date</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="range"
                    checked={blockType === 'range'}
                    onChange={(e) => setBlockType(e.target.value)}
                    className="text-[#8B2635] focus:ring-[#8B2635]"
                  />
                  <span className="text-sm text-[#2D2D2D]">Date Range</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="time"
                    checked={blockType === 'time'}
                    onChange={(e) => setBlockType(e.target.value)}
                    className="text-[#8B2635] focus:ring-[#8B2635]"
                  />
                  <span className="text-sm text-[#2D2D2D]">Specific Time Slot</span>
                </label>
              </div>
              
              {blockType === 'single' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#6B6B6B] mb-2">
                    Date to Block
                  </label>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#6B6B6B]" />
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      min={getMinDate()}
                      className="flex-1 p-2 border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B2635]/40 focus:border-[#8B2635]"
                      required
                    />
                  </div>
                </div>
              )}

              {blockType === 'range' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-[#6B6B6B] mb-2">
                      Start Date
                    </label>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#6B6B6B]" />
                      <input
                        type="date"
                        value={dateRange.start}
                        onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                        min={getMinDate()}
                        className="flex-1 p-2 border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B2635]/40 focus:border-[#8B2635]"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#6B6B6B] mb-2">
                      End Date
                    </label>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#6B6B6B]" />
                      <input
                        type="date"
                        value={dateRange.end}
                        onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                        min={dateRange.start || getMinDate()}
                        className="flex-1 p-2 border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B2635]/40 focus:border-[#8B2635]"
                        required
                      />
                    </div>
                  </div>
                </div>
              )}

              {blockType === 'time' && (
                <div className="space-y-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-[#6B6B6B] mb-2">
                      Date
                    </label>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#6B6B6B]" />
                      <input
                        type="date"
                        value={timeBlockData.date}
                        onChange={(e) => setTimeBlockData({ ...timeBlockData, date: e.target.value })}
                        min={getMinDate()}
                        className="flex-1 p-2 border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B2635]/40 focus:border-[#8B2635]"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#6B6B6B] mb-2">
                      Time Slot
                    </label>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[#6B6B6B]" />
                      <select
                        value={timeBlockData.time}
                        onChange={(e) => setTimeBlockData({ ...timeBlockData, time: e.target.value })}
                        className="flex-1 p-2 border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B2635]/40 focus:border-[#8B2635] bg-white"
                        required
                      >
                        <option value="">Select a time slot</option>
                        {timeSlots.map((time) => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Reason field for all types */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#6B6B6B] mb-2">
                  Reason (Optional)
                </label>
                <input
                  type="text"
                  value={blockType === 'time' ? timeBlockData.reason : reason}
                  onChange={(e) => {
                    if (blockType === 'time') {
                      setTimeBlockData({ ...timeBlockData, reason: e.target.value });
                    } else {
                      setReason(e.target.value);
                    }
                  }}
                  placeholder="e.g., Private event, Maintenance, Fully booked, etc."
                  className="w-full p-2 border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B2635]/40 focus:border-[#8B2635]"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#8B2635] hover:bg-[#B23B4A] text-white"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Blocking...
                  </>
                ) : blockType === 'single' ? (
                  'Block Single Date'
                ) : blockType === 'range' ? (
                  'Block Date Range'
                ) : (
                  'Block Time Slot'
                )}
              </Button>
            </form>

            {/* List of Blocked Items */}
            <div>
              <h3 className="text-lg font-semibold text-[#2D2D2D] mb-4">Blocked Items</h3>
              
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-[#8B2635]" />
                </div>
              ) : blockedDates.length === 0 && blockedRanges.length === 0 && blockedTimes.length === 0 ? (
                <div className="text-center py-8 bg-[#F9F9F9] rounded-lg border border-dashed border-[#E5E5E5]">
                  <CalendarClock className="w-12 h-12 text-[#CCCCCC] mx-auto mb-3" />
                  <p className="text-[#6B6B6B]">No blocked dates, ranges, or time slots</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Blocked Time Slots Section */}
                  {blockedTimes.length > 0 && (
                    <div>
                      <h4 className="text-md font-semibold text-[#8B2635] mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Blocked Time Slots ({blockedTimes.length})
                      </h4>
                      <div className="space-y-3">
                        {groupedBlockedTimes().map((group) => (
                          <div key={group.date} className="bg-[#F9F9F9] rounded-lg border border-[#E5E5E5] overflow-hidden">
                            <div className="bg-[#8B2635]/10 px-4 py-2 border-b border-[#E5E5E5]">
                              <p className="font-medium text-[#8B2635] flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                {format(parseISO(group.date), 'EEEE, MMMM d, yyyy')}
                              </p>
                            </div>
                            <div className="p-3">
                              {group.times.map((item) => (
                                <div
                                  key={item.id}
                                  className="flex items-center justify-between py-2 border-b border-[#E5E5E5] last:border-b-0"
                                >
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3">
                                      <Clock className="w-4 h-4 text-[#8B2635]" />
                                      <span className="font-medium text-[#2D2D2D]">
                                        {item.time}
                                      </span>
                                    </div>
                                    {item.reason && (
                                      <p className="text-sm text-[#6B6B6B] mt-1 ml-7">
                                        Reason: {item.reason}
                                      </p>
                                    )}
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(item.id, 'time')}
                                    disabled={deletingId === item.id}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    {deletingId === item.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Trash2 className="w-4 h-4" />
                                    )}
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Single Dates Section */}
                  {blockedDates.length > 0 && (
                    <div>
                      <h4 className="text-md font-semibold text-[#8B2635] mb-3 flex items-center gap-2">
                        <CalendarDays className="w-4 h-4" />
                        Blocked Dates ({blockedDates.length})
                      </h4>
                      <div className="space-y-3">
                        {blockedDates.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center justify-between p-4 bg-[#F9F9F9] rounded-lg border border-[#E5E5E5] hover:border-[#8B2635]/20 transition-colors"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <Calendar className="w-4 h-4 text-[#8B2635]" />
                                <span className="font-medium text-[#2D2D2D]">
                                  {format(parseISO(item.date), 'EEEE, MMMM d, yyyy')}
                                </span>
                              </div>
                              {item.reason && (
                                <p className="text-sm text-[#6B6B6B] mt-1 ml-7">
                                  Reason: {item.reason}
                                </p>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(item.id, 'date')}
                              disabled={deletingId === item.id}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              {deletingId === item.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Date Ranges Section */}
                  {blockedRanges.length > 0 && (
                    <div>
                      <h4 className="text-md font-semibold text-[#8B2635] mb-3 flex items-center gap-2">
                        <CalendarRange className="w-4 h-4" />
                        Date Ranges ({blockedRanges.length})
                      </h4>
                      <div className="space-y-3">
                        {blockedRanges.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center justify-between p-4 bg-[#F9F9F9] rounded-lg border-l-4 border-l-[#8B2635] border border-[#E5E5E5] hover:border-[#8B2635]/20 transition-colors"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <CalendarRange className="w-4 h-4 text-[#8B2635]" />
                                <span className="font-medium text-[#2D2D2D]">
                                  {format(parseISO(item.start_date), 'MMM d, yyyy')} - {format(parseISO(item.end_date), 'MMM d, yyyy')}
                                </span>
                                <span className="text-xs bg-[#8B2635]/10 text-[#8B2635] px-2 py-1 rounded-full">
                                  {item.range_days} days
                                </span>
                              </div>
                              {item.reason && (
                                <p className="text-sm text-[#6B6B6B] mt-1 ml-7">
                                  Reason: {item.reason}
                                </p>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(item.id, 'range')}
                              disabled={deletingId === item.id}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              {deletingId === item.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}